import React from 'react';
import SidebarTemplate from 'components/templates/SidebarTemplate';
import FormContents from 'components/atoms/Form/FormContents';
import { Container, Row, Col } from 'react-grid-system';
import moment from 'moment';
import BodyText from 'components/atoms/BodyText';
import Button, { PrimaryButton } from 'components/atoms/Button';
import ConfirmModal from 'components/organismos/ConfirmModal';
import TextForm from 'components/molecules/TextForm';
import FormField from 'components/atoms/Form/FormField';
import BlockUI from 'components/molecules/BlockUi';
import { productColor, textFontSize, textColor } from 'components/styles';
import FlexBox from 'components/atoms/FlexBox';
import FormSubmitArea from 'components/atoms/Form/FormSubmitArea';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Store } from 'modules/store';
import { AllowanceDetail, AllowanceHistory } from 'domain/master/attend/attendAllowanceApplication';
import {
  AllowanceApplicationForm, BUTTON_TYPE_APPLY, BUTTON_TYPE_CANCEL, BUTTON_TYPE_DELETE,
} from './hooks';

const AllowanceApplicationPage: React.FC = () => {
  const history = useHistory();
  const {
    formik,
    targetDate,
    confirmApplyModalOpen,
    closeConfirmApplyModal,
    setDetailValue,
    setDetailComment,
    isLoading,
    dispStaffName,
    isGetAllowanceSuccess,
  } = AllowanceApplicationForm();

  const roleScreen = useSelector((state: Store) => state.auth.roleScreen);

  const headerStyle = {
    padding: '4px 16px',
    backgroundColor: productColor.primaryM95,
    fontWeight: 'normal' as const,
    textAlign: 'center' as const,
    fontSize: textFontSize.re,
    borderTop: `1px solid ${productColor.primaryM80}`,
    borderBottom: `1px solid ${productColor.primaryM80}`,
    color: textColor.main,
  };

  return (
    <SidebarTemplate pageTitle="手当申請">
      <BlockUI blocking={isLoading}>
        <FormContents>
          <form onSubmit={formik.handleSubmit}>
            <Container fluid={true}>

              <Row>
                <Col md={2} xs={12}><BodyText>対象日</BodyText></Col>
                <Col md={6} xs={12}>
                  <BodyText>{moment(`${targetDate}`).format('YYYY年MM月DD日(ddd)')}</BodyText>
                </Col>
              </Row>
              <Row>
                <Col md={2} xs={12}><BodyText>スタッフ</BodyText></Col>
                <Col md={6} xs={12}><BodyText>{dispStaffName}</BodyText></Col>
              </Row>
              {/* 出勤組織は表示のみ。遷移元（打刻データ確認・日次／月次シフト）が渡した組織で固定する */}
              <Row>
                <Col md={2} xs={12}><BodyText>出勤組織</BodyText></Col>
                <Col md={6} xs={12}><BodyText>{formik.values.orgName}</BodyText></Col>
              </Row>

              {/* 手当テーブル。明細が左寄せなのでヘッダーも左寄せ（headerStyle は更新履歴と共用で中央寄せのため、ここだけ上書き） */}
              <Row style={{ ...headerStyle, textAlign: 'left' }}>
                <Col xs={3} md={3}>手当名</Col>
                <Col xs={3} md={3}>申請内容</Col>
                <Col xs={3} md={2}>申請値</Col>
                <Col xs={3} md={4}>備考</Col>
              </Row>

              {
                formik.values.detailList.length === 0 && (
                  <Row style={{ textAlign: 'center', fontSize: textFontSize.re }}>
                    <Col xs={12}>
                      <BodyText>
                        申請できる手当がありません。雇用形態設定で対象の手当を選択してください。
                      </BodyText>
                    </Col>
                  </Row>
                )
              }

              {
                formik.values.detailList.map((detail: AllowanceDetail) => (
                  <Row
                    key={detail.allowanceId}
                    style={{ fontSize: textFontSize.re, borderBottom: `1px solid ${productColor.primaryM95}` }}
                  >
                    <Col xs={3} md={3} style={{ display: 'flex', alignItems: 'center' }}>
                      {detail.allowanceName}
                    </Col>
                    <Col xs={3} md={3} style={{ display: 'flex', alignItems: 'center' }}>
                      {detail.dispMessage}
                    </Col>
                    <Col xs={3} md={2} style={{ display: 'flex', alignItems: 'center' }}>
                      <FlexBox>
                        <div style={{ maxWidth: '110px' }}>
                          <TextForm
                            name={`allowanceValue_${detail.allowanceId}`}
                            label=""
                            value={detail.allowanceValue === null ? '' : String(detail.allowanceValue)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setDetailValue(detail.allowanceId, e.target.value);
                            }}
                          />
                        </div>
                        <span style={{ margin: '0 5px' }}><b>{detail.dispUnit}</b></span>
                      </FlexBox>
                    </Col>
                    <Col xs={3} md={4} style={{ display: 'flex', alignItems: 'center' }}>
                      <TextForm
                        name={`comment_${detail.allowanceId}`}
                        label=""
                        value={detail.comment || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setDetailComment(detail.allowanceId, e.target.value);
                        }}
                      />
                    </Col>
                  </Row>
                ))
              }

              <Row>
                <Col xs={12}>
                  <FormField>
                    <TextForm
                      name="applicationReason"
                      label="申請理由（任意）"
                      value={formik.values.applicationReason || ''}
                      onChange={formik.handleChange}
                    />
                  </FormField>
                </Col>
              </Row>

              {/* 否決されている場合は否決理由を表示する */}
              {
                formik.values.applicationStatus === 2 && (
                  <Row>
                    <Col xs={12}>
                      <BodyText color={productColor.deleteAction}>
                        {`否決理由：${formik.values.rejectReason || ''}`}
                      </BodyText>
                    </Col>
                  </Row>
                )
              }

              <Row>
                <Col xs={12} md={12}>
                  <FormSubmitArea>
                    {
                      (isGetAllowanceSuccess
                        && (formik.values.modifiyStatus === null || formik.values.modifiyStatus === 0
                          || (formik.values.modifiyStatus === 1 && formik.values.applicationStatus !== 0))
                        && roleScreen && (roleScreen.editable === 1 || roleScreen.length === 0)) && (
                        <div style={{ marginRight: '12px' }}>
                          {/* 申請中でも押せること。版を据え置いて上書き＋明細の洗い替えを行う導線がここしかない */}
                          <PrimaryButton
                            ghost={false}
                            text="申請"
                            onClick={() => {
                              formik.setFieldValue('buttonType', BUTTON_TYPE_APPLY);
                              formik.handleSubmit();
                            }}
                          />
                        </div>
                      )
                    }
                    {
                      (isGetAllowanceSuccess && formik.values.applicationStatus === 0
                        && roleScreen && (roleScreen.editable === 1 || roleScreen.length === 0)) && (
                        <div style={{ marginRight: '12px' }}>
                          <PrimaryButton
                            ghost={true}
                            text="申請キャンセル"
                            onClick={() => {
                              formik.setFieldValue('buttonType', BUTTON_TYPE_CANCEL);
                              formik.handleSubmit();
                            }}
                          />
                        </div>
                      )
                    }
                    {
                      (isGetAllowanceSuccess && formik.values.deleteAvailable
                        && roleScreen && (roleScreen.editable === 1 || roleScreen.length === 0)) && (
                        <div style={{ marginRight: '12px' }}>
                          <PrimaryButton
                            ghost={true}
                            text="削除申請"
                            onClick={() => {
                              formik.setFieldValue('buttonType', BUTTON_TYPE_DELETE);
                              formik.handleSubmit();
                            }}
                          />
                        </div>
                      )
                    }
                    <Button
                      text="戻る"
                      ghost={true}
                      onClick={() => {
                        history.push(sessionStorage.getItem('attendStampList.returnDestination') || '');
                      }}
                    />
                  </FormSubmitArea>
                </Col>
              </Row>

              {/* 更新履歴 */}
              {
                formik.values.historyList.length > 0 && (
                  <>
                    <Row style={headerStyle}>
                      <Col xs={12}>更新履歴</Col>
                    </Row>
                    <Row style={headerStyle}>
                      <Col xs={2}>版</Col>
                      <Col xs={2}>申請日時</Col>
                      <Col xs={2}>申請者</Col>
                      <Col xs={2}>種別</Col>
                      <Col xs={2}>状態</Col>
                      <Col xs={2}>否決理由</Col>
                    </Row>
                    {
                      formik.values.historyList.map((history1: AllowanceHistory) => (
                        <Row key={`ver${history1.version}`} style={{ fontSize: textFontSize.re, textAlign: 'center' }}>
                          <Col xs={2}>{`ver.${history1.version}`}</Col>
                          <Col xs={2}>
                            {history1.applicationDate
                              ? moment(history1.applicationDate).format('YYYY/MM/DD HH:mm') : ''}
                          </Col>
                          <Col xs={2}>{history1.applicationStaffName}</Col>
                          <Col xs={2}>{history1.modifiyStatus === 1 ? '削除申請' : '申請'}</Col>
                          <Col xs={2}>
                            {history1.applicationStatus === 0 && '申請中'}
                            {history1.applicationStatus === 1 && '承認'}
                            {history1.applicationStatus === 2 && '否決'}
                          </Col>
                          <Col xs={2}>{history1.rejectReason || ''}</Col>
                        </Row>
                      ))
                    }
                  </>
                )
              }

            </Container>
          </form>
        </FormContents>

        <ConfirmModal
          open={confirmApplyModalOpen}
          closeHandler={closeConfirmApplyModal}
          onSubmit={formik.handleSubmit}
          title="確認メッセージ"
          // 打刻修正申請（StampModificationPage）と同じ文言。押したボタンの種別で出し分ける
          content={
            // eslint-disable-next-line no-nested-ternary
            formik.values.buttonType === BUTTON_TYPE_CANCEL ? '申請をキャンセルします。よろしいですか？'
              : formik.values.buttonType === BUTTON_TYPE_DELETE ? '削除申請をします。よろしいですか？'
                : '申請します。よろしいですか？'
          }
          submitText={
            // eslint-disable-next-line no-nested-ternary
            formik.values.buttonType === BUTTON_TYPE_CANCEL ? '申請キャンセル'
              : formik.values.buttonType === BUTTON_TYPE_DELETE ? '削除申請'
                : '申請'
          }
        />
      </BlockUI>
    </SidebarTemplate>
  );
};

export default AllowanceApplicationPage;
